import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Autocomplete } from '@/modules/common';
import { MultiSelect } from '@/modules/common/components/multi-select';
import { OptionType } from '@/modules/common/types';
import { positionOptions } from '@/modules/events/constants';

import { EventModelValues } from '../types/model';
import { ModelDataType } from '../types/model';
import { addEventModelSchema } from '../validations/add-model';

interface AddEditModelDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (
    model: EventModelValues & {
      sizes: {
        sizeId: string;
        quantity: number;
        reservedQuantity: number;
        value: string;
      }[];
    }
  ) => void;
  onEdit: (model: EventModelValues) => void;
  modelOptions: OptionType[];
  sizeOptions: OptionType[];
  selectedModel: ModelDataType | null;
}

export function AddEditModelDialog({
  isOpen,
  onOpenChange,
  onAdd,
  onEdit,
  modelOptions,
  sizeOptions,
  selectedModel,
}: AddEditModelDialogProps) {
  const isEdit = selectedModel !== null;

  const form = useForm<EventModelValues>({
    resolver: zodResolver(addEventModelSchema),
    defaultValues: {
      modelId: '',
      sizeId: '',
      position: [],
      quantity: undefined,
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, defaultValues },
  } = form;

  const [selectedModelId, selectedPositions] = watch(['modelId', 'position']);

  const selectedModelOption = modelOptions?.find(
    (option) => option.id === selectedModelId
  );
  const availablePositions = positionOptions.filter((option) =>
    selectedModelOption?.sides.includes(option.value)
  );

  const handleClose = async (open: boolean) => {
    onOpenChange(open);
    reset(defaultValues);
  };

  const handleFormSubmit = async (values: EventModelValues) => {
    if (isEdit) {
      onEdit(values);
    } else {
      const size = sizeOptions.find((option) => option.id === values.sizeId);

      onAdd({
        modelId: values.modelId,
        position: values.position,
        sizes: [
          {
            sizeId: size.id,
            quantity: values.quantity,
            reservedQuantity: 0,
            value: size.value,
          },
        ],
      });
    }

    handleClose(false);
  };

  useEffect(() => {
    form.reset(isEdit ? selectedModel : defaultValues);
  }, [isEdit]);

  useEffect(() => {
    if (!selectedModelId) {
      return;
    }

    const matchingPositions = availablePositions.filter((option) =>
      selectedPositions.includes(option.value)
    );

    if (matchingPositions.length === selectedPositions.length) {
      return;
    }

    form.setValue(
      'position',
      matchingPositions.map((option) => option.value)
    );
  }, [selectedModelId]);

  return (
    <Dialog onOpenChange={handleClose} open={isOpen} modal={false}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Model Details' : 'Add Model'}</DialogTitle>
          <DialogDescription>Description</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleSubmit(handleFormSubmit)(event);
            }}
          >
            <div className="flex flex-col gap-4">
              <Autocomplete
                label="Type"
                name="modelId"
                control={control}
                searchable
                placeholder="Select type"
                options={modelOptions}
              />

              {selectedModelId && (
                <MultiSelect
                  name="position"
                  label="Position"
                  control={control}
                  placeholder="Select position"
                  options={availablePositions}
                  maxCount={3}
                />
              )}

              <Autocomplete
                label="Size"
                name="sizeId"
                control={control}
                searchable
                placeholder="Select size"
                options={sizeOptions}
              />

              <FormField
                control={control}
                name="quantity"
                render={({ field, formState: { errors } }) => (
                  <FormItem>
                    <FormLabel>Quantity</FormLabel>

                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter quantity"
                        error={!!errors?.quantity}
                        {...field}
                      />
                    </FormControl>

                    {!errors?.quantity && (
                      <p className="text-xs text-muted-foreground">
                        Set value from 1 to 100
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mt-6 gap-4">
              <DialogClose asChild>
                <Button variant="outline" data-testid="cancel-button">
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="submit"
                data-testid="save-button"
                disabled={!isDirty}
              >
                {isEdit ? 'Save' : 'Add model'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
